import { useEffect, useState } from 'react';
import { useAuth } from '../components/AuthGuard';
import { useNavigate } from 'react-router-dom';

// ─── DIS Nexus Gateway Landing ────────────────────────────────────────────────
// Lightweight login gateway matching Site A's design language.
// Outfit font · Crimson #8B1A2D · Off-white background · Gold accents
// Zero 3D, zero heavy animations, zero external image dependencies.
// ─────────────────────────────────────────────────────────────────────────────

const BlankLanding = () => {
    const { user, loading, login } = useAuth();
    const navigate = useNavigate();
    const [isSigningIn, setIsSigningIn] = useState(false);
    const [error, setError] = useState('');

    // If already authenticated, redirect straight to dashboard
    useEffect(() => {
        if (!loading && user) {
            navigate('/dashboard');
        }
    }, [user, loading, navigate]);

    const handleLogin = async () => {
        setIsSigningIn(true);
        setError('');
        try {
            await login();
        } catch (e) {
            setError('Sign-in failed. Please try again.');
            setIsSigningIn(false);
        }
    };

    if (loading) {
        return (
            <div style={styles.loadingScreen}>
                <div style={styles.spinner} />
            </div>
        );
    }

    return (
        <div style={styles.page}>

            {/* ── Subtle background pattern ── */}
            <div style={styles.bgPattern} />

            {/* ── Top bar ── */}
            <header style={styles.header}>
                <div style={styles.logoRow}>
                    <img
                        src="/dis_logo.svg"
                        alt="DIS Logo"
                        style={styles.logoImg}
                    />
                    <div>
                        <div style={styles.logoName}>Dynamic InduTechno Solutions</div>
                    </div>
                </div>
            </header>

            {/* ── Main card ── */}
            <main style={styles.main}>
                <div style={styles.card}>

                    {/* DIS Logo */}
                    <div style={styles.iconWrap}>
                        <img
                            src="/dis_logo_crimson.svg"
                            alt="DIS"
                            style={{ width: 44, height: 44, objectFit: 'contain' }}
                        />
                    </div>

                    {/* Labels */}
                    <div style={styles.systemLabel}>NEXUS ACCESS</div>
                    <h1 style={styles.title}>Nexus</h1>
                    <p style={styles.subtitle}>
                        Dynamic InduTechno Solutions
                    </p>

                    {/* Divider */}
                    <div style={styles.divider} />

                    {/* Sign-in button */}
                    <button
                        onClick={handleLogin}
                        disabled={isSigningIn}
                        style={{
                            ...styles.signInBtn,
                            opacity: isSigningIn ? 0.7 : 1,
                            cursor: isSigningIn ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={e => {
                            if (!isSigningIn) e.currentTarget.style.background = '#5E0F1D';
                        }}
                        onMouseLeave={e => {
                            if (!isSigningIn) e.currentTarget.style.background = '#8B1A2D';
                        }}
                    >
                        {/* Google G icon */}
                        {!isSigningIn && (
                            <svg width="18" height="18" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                            </svg>
                        )}
                        {isSigningIn
                            ? <span>Signing in…</span>
                            : <span>Sign in with Google</span>
                        }
                    </button>

                    {/* Error */}
                    {error && <p style={styles.error}>{error}</p>}

                    {/* Security notice */}
                    <p style={styles.notice}>
                        Access is restricted to authorised DIS staff only.<br />
                        Unauthorised access attempts are logged and monitored.
                    </p>

                </div>

                {/* Accreditation row */}
                <div style={styles.accredRow}>
                    <span style={styles.accredText}>DIS · SUNY Potsdam · NEF</span>
                    <span style={styles.accredDot}>·</span>
                    <span style={styles.accredText}>nexus.dynamicindutechno.solutions</span>
                </div>
            </main>

        </div>
    );
};

// ─── Inline styles — matching Site A design tokens ────────────────────────────
const styles = {
    page: {
        minHeight: '100vh',
        background: '#8B1A2D',
        fontFamily: "'Outfit', sans-serif",
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
    },
    bgPattern: {
        position: 'absolute',
        inset: 0,
        backgroundImage: `
            radial-gradient(circle at 15% 50%, rgba(0,0,0,0.15) 0%, transparent 50%),
            radial-gradient(circle at 85% 20%, rgba(201,151,58,0.12) 0%, transparent 40%)
        `,
        pointerEvents: 'none',
        zIndex: 0,
    },
    loadingScreen: {
        minHeight: '100vh',
        background: '#8B1A2D',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    spinner: {
        width: 36,
        height: 36,
        border: '3px solid #E8E4E0',
        borderTop: '3px solid #8B1A2D',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    header: {
        padding: '24px 40px',
        position: 'relative',
        zIndex: 1,
    },
    logoRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
    },
    logoImg: {
        width: 40,
        height: 40,
        objectFit: 'contain',
    },
    logoName: {
        fontSize: 13,
        fontWeight: 700,
        color: 'rgba(255,255,255,0.9)',
        letterSpacing: '-0.01em',
        lineHeight: 1.2,
        maxWidth: 180,
    },
    main: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px 80px',
        position: 'relative',
        zIndex: 1,
    },
    card: {
        width: '100%',
        maxWidth: 420,
        background: '#FFFFFF',
        borderRadius: 24,
        padding: '48px 40px',
        boxShadow: '0 20px 60px rgba(139,26,45,0.10), 0 2px 12px rgba(139,26,45,0.06)',
        border: '1px solid #E8E4E0',
        textAlign: 'center',
    },
    iconWrap: {
        width: 64,
        height: 64,
        borderRadius: 16,
        background: 'rgba(139,26,45,0.08)',
        border: '1px solid rgba(139,26,45,0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 24px',
    },
    systemLabel: {
        fontSize: 10,
        fontWeight: 700,
        color: '#8B1A2D',
        textTransform: 'uppercase',
        letterSpacing: '0.18em',
        marginBottom: 10,
    },
    title: {
        fontSize: 32,
        fontWeight: 800,
        color: '#1A1210',
        letterSpacing: '-0.02em',
        lineHeight: 1.1,
        marginBottom: 8,
    },
    subtitle: {
        fontSize: 14,
        fontWeight: 400,
        color: '#6B6560',
        lineHeight: 1.5,
        marginBottom: 0,
    },
    divider: {
        height: 1,
        background: '#E8E4E0',
        margin: '28px 0',
    },
    signInBtn: {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '14px 24px',
        background: '#8B1A2D',
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: 700,
        fontFamily: "'Outfit', sans-serif",
        border: 'none',
        borderRadius: 12,
        transition: 'background 0.25s ease, transform 0.15s ease',
        letterSpacing: '0.02em',
    },
    error: {
        marginTop: 14,
        fontSize: 13,
        color: '#C53030',
        fontWeight: 500,
    },
    notice: {
        marginTop: 24,
        fontSize: 11,
        color: '#C0BDB8',
        lineHeight: 1.7,
        fontWeight: 400,
    },
    accredRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginTop: 32,
    },
    accredText: {
        fontSize: 11,
        color: 'rgba(255,255,255,0.4)',
        fontWeight: 500,
        letterSpacing: '0.05em',
    },
    accredDot: {
        color: '#E8BC6A',
        fontSize: 14,
    },
};

// Inject keyframe for spinner
const styleTag = document.createElement('style');
styleTag.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleTag);

export default BlankLanding;
